import {
  AddIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CloseIcon,
  DeleteIcon,
  EditIcon,
} from '@chakra-ui/icons';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Checkbox,
  Collapse,
  Flex,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DailyAlertIcon from '../../assets/icons/alert-icon-small.svg';
import { useCustomFields } from '../../hooks/useCustomFields';
import { usePermissions } from '../../hooks/usePermissions';
import {
  generateAlertLink,
  getUserInfoFromToken,
  notifyCHSOnSubmission,
  notifyDenimOnApproval,
  notifyDenimOnSave,
} from '../../services/notifications/transferOutcomesNotifications';
import { updatePracticeById } from '../../services/practices/updatePracticeById';
import { updateProviderById } from '../../services/providers/updateProviderById';
import { CustomFieldValue, Practice, Provider } from '../../types';
import { CustomField } from '../../types/custom-fields/types';
import {
  CommentLogEntry,
  createCommentLogEntry,
  createDefaultTransferOutcomes,
  getCommentLog,
  getTransferOutcomes,
  TransferOutcomesData,
  TransferOutcomesMetadata,
} from '../../types/transferOutcomes';
import { analytics } from '../../utils/analytics';
import { CATEGORY_LEGEND } from '../../utils/categoryLegend';
import {
  findDailyAlertField,
  getClientIdWithFallback,
} from '../../utils/customFieldUtils';
import {
  clearProcessedAlertId,
  clearProcessingTimeout,
  isAlertIdProcessed,
  markAlertIdAsProcessed,
  setProcessingTimeout,
} from '../../utils/deepLinkCoordinator';
import { getVisibleText } from '../../utils/textConversion';
import NotePreview from '../note-preview/NotePreview';
import RichTextEditor from '../rich-text-editor/RichTextEditor';
import TransferOutcomesConfig from '../transfer-outcomes/TransferOutcomesConfig';

interface DailyAlertCardProps {
  width?: string;
  practice?: Practice;
  provider?: Provider;
  isLoading?: boolean;
  customFields?: CustomField[]; // Add customFields prop
  practices?: Array<{ id: string; name: string }>; // Add practices prop for fallback
  specialtyOptions?: { label: string; value: string }[]; // Specialty options from parent
  isSpecialtyOptionsLoading?: boolean; // Loading state for specialty options
  isCustomFieldsLoading?: boolean; // Add loading state for custom fields
  customFieldsError?: string | null; // Add error state for custom fields
}

// Category color mapping
const getCategoryColor = (category: string): string => {
  const colorMap: Record<string, string> = {
    ADMIN_INFO: 'gray',
    CLINIC_CLOSED: 'red',
    COVID_POLICY: 'orange',
    INMATE_TRANSFER: 'purple',
    INSURANCE_NOTICE: 'blue',
    LIMITED_AVAILABILITY: 'yellow',
    NOT_ON_PAC_PRACTICE: 'pink',
    NOT_ON_PAC_PROVIDER: 'pink',
    NOT_ON_PAC_SPECIALTY_SERVICE: 'pink',
    OOO: 'red',
    PRACTICE_LOCATION_CHANGE: 'cyan',
    PROVIDER_LOCATION_CHANGE: 'cyan',
    PROVIDER_START_END: 'teal',
    SCHEDULING_RULE: 'cyan',
    SERVICE_SCOPE: 'purple',
    UTILITY_ISSUES: 'orange',
    VIRTUAL_WAITING_ROOM: 'yellow',
  };
  return colorMap[category] || 'gray';
};

const DailyAlertCard: React.FC<DailyAlertCardProps> = ({
  practice,
  provider,
  width,
  customFields: passedCustomFields,
  practices,
  specialtyOptions = [],
  isSpecialtyOptionsLoading = false,
  isCustomFieldsLoading: passedCustomFieldsLoading = false,
  customFieldsError: passedCustomFieldsError = null,
}) => {
  // Helper to get practice name by id from provider.relatedPractices or practices prop
  const getPracticeNameById = (
    id:
      | string
      | string[]
      | { providerId: string; providerLabel: string }[],
  ): string => {
    let practiceId = '';

    // Handle different types of id
    if (typeof id === 'string') {
      practiceId = id;
    } else if (Array.isArray(id)) {
      if (id.length > 0) {
        const firstItem = id[0];
        if (typeof firstItem === 'string') {
          practiceId = firstItem;
        } else if (
          typeof firstItem === 'object' &&
          'providerId' in firstItem
        ) {
          practiceId = firstItem.providerId;
        }
      }
    }

    if (!practiceId) {
      return 'Unknown Practice';
    }

    // First try to find in provider.relatedPractices
    const practiceFromProvider = provider?.relatedPractices?.find(
      (p) => p.id === practiceId,
    );
    if (practiceFromProvider) {
      return practiceFromProvider.name;
    }

    // Fallback to practices prop if available
    const practiceFromProp = practices?.find(
      (p) => p.id === practiceId,
    );
    if (practiceFromProp) {
      return practiceFromProp.name;
    }

    return 'Unknown Practice';
  };
  const [alerts, setAlerts] = useState<CustomFieldValue[]>([]);
  const [editingAlert, setEditingAlert] =
    useState<CustomFieldValue | null>(null);
  const [tempAlert, setTempAlert] = useState<string>('');
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [fieldId, setFieldId] = useState<string | null>(null);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const [alertToDelete, setAlertToDelete] =
    useState<CustomFieldValue | null>(null);
  const [neverExpire, setNeverExpire] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [associateWithPractice, setAssociateWithPractice] =
    useState(false);
  const [selectedPracticeId, setSelectedPracticeId] =
    useState<string>('');
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [selectedCategories, setSelectedCategories] = useState<
    string[]
  >([]);
  // Transfer outcomes state
  const [transferOutcomes, setTransferOutcomes] =
    useState<TransferOutcomesData>(createDefaultTransferOutcomes());
  const [comment, setComment] = useState<string>('');
  const [commentLog, setCommentLog] = useState<CommentLogEntry[]>([]);
  const [isSubmittingForApproval, setIsSubmittingForApproval] =
    useState<boolean>(false);
  const [isApproving, setIsApproving] = useState<boolean>(false);
  const [isAddingComment, setIsAddingComment] =
    useState<boolean>(false);
  const [localSpecialtyLoading, setLocalSpecialtyLoading] =
    useState<boolean>(false);
  const hasSetLoadingForThisModalOpen = useRef<boolean>(false);

  // Get user permissions
  const {
    canEditDailyNotes,
    canEditOfficeAlerts,
    isAdmin,
    isReady: permissionsReady,
  } = usePermissions();

  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const {
    isOpen: isCategoryLegendOpen,
    onClose: onCategoryLegendClose,
  } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Helper to get entity name (practice or provider)
  const getEntityName = (): string => {
    if (practice) {
      return practice.name;
    }
    if (provider) {
      const firstName =
        provider.firstName || provider.firstname || '';
      const lastName = provider.lastName || provider.lastname || '';
      return `${firstName} ${lastName}`.trim() || 'Unknown Provider';
    }
    return 'Unknown Entity';
  };
  // Normalize and dedupe by id
  const normalizeAlerts = (
    items: CustomFieldValue[] | undefined | null,
  ) => {
    const unique: Record<string, CustomFieldValue> = {};
    (items || []).forEach((item) => {
      if (item && item.id) {
        if (!unique[item.id]) unique[item.id] = item;
      }
    });
    return Object.values(unique);
  };
  // Use passed customFields if available, otherwise fetch them
  const clientId = practice?.clientId || getClientIdWithFallback();
  const entityType = practice ? 'Practice' : 'ProviderData';

  // Use custom hook to fetch custom fields ONLY if not passed as prop
  const {
    customFields: fetchedCustomFields,
    loading: isFieldIdLoading,
    error: customFieldsError,
    refresh: refreshCustomFields,
  } = useCustomFields(
    passedCustomFields ? null : clientId, // Don't fetch if customFields are passed
    passedCustomFields ? null : entityType,
  );

  const requestedFieldRefreshRef = useRef<boolean>(false);

  // Normalize helper for arrays or map-shaped custom fields
  const normalizeCustomFieldsToArray = (
    fields:
      | CustomField[]
      | Record<string, CustomField>
      | Record<string, unknown>
      | undefined
      | null,
  ): CustomField[] | undefined => {
    if (!fields) return undefined;
    if (Array.isArray(fields)) return fields;
    return Object.values(fields) as CustomField[];
  };

  const providerCustomFieldsSnake = (
    provider as
      | { custom_fields?: Record<string, unknown> }
      | undefined
  )?.custom_fields;

  // Prefer passed/fetched custom fields; fall back to entity payload
  const effectiveCustomFields = React.useMemo<CustomField[]>(() => {
    const fromPassed = normalizeCustomFieldsToArray(
      passedCustomFields,
    );
    if (fromPassed?.length) return fromPassed;

    const fromFetched = normalizeCustomFieldsToArray(
      fetchedCustomFields,
    );
    if (fromFetched?.length) return fromFetched;

    const fromPracticeCamel = normalizeCustomFieldsToArray(
      practice?.customFields as unknown as Record<string, unknown>,
    );
    if (fromPracticeCamel?.length) return fromPracticeCamel;

    const fromPracticeSnake = normalizeCustomFieldsToArray(
      practice?.custom_fields as unknown as Record<string, unknown>,
    );
    if (fromPracticeSnake?.length) return fromPracticeSnake;

    const fromProviderCamel = normalizeCustomFieldsToArray(
      provider?.customFields as unknown as Record<string, unknown>,
    );
    if (fromProviderCamel?.length) return fromProviderCamel;

    const fromProviderSnake = normalizeCustomFieldsToArray(
      providerCustomFieldsSnake,
    );
    if (fromProviderSnake?.length) return fromProviderSnake;

    return [];
  }, [
    passedCustomFields,
    fetchedCustomFields,
    practice?.customFields,
    practice?.custom_fields,
    provider,
    providerCustomFieldsSnake,
  ]);

  const dailyAlertFieldId = React.useMemo<string | null>(() => {
    const field = findDailyAlertField(effectiveCustomFields);
    return field?.id ?? null;
  }, [effectiveCustomFields]);

  // Get Notes-Category field for multiselect (commented out - category selection UI is disabled)
  // const notesCategoryField = customFields.find(
  //   (field) =>
  //     field.name === 'Notes-Category' ||
  //     field.field_type === 'Notes-Category'
  // );

  // const categoryOptions = notesCategoryField?.allowed_values
  //   ? [...notesCategoryField.allowed_values]
  //       .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
  //       .map((val) => {
  //         const friendlyName =
  //           CATEGORY_LEGEND[val.value as keyof typeof CATEGORY_LEGEND]
  //             ?.name;
  //         const fallbackName = val.value
  //           .replace(/_/g, ' ')
  //           .replace(/\b\w/g, (l) => l.toUpperCase());

  //         // Debug logging to see what's happening
  //         if (!friendlyName) {
  //           console.log(
  //             'Category not found in legend:',
  //             val.value,
  //             'using fallback:',
  //             fallbackName
  //           );
  //         }

  //         return {
  //           label: friendlyName || fallbackName,
  //           value: val.value,
  //         };
  //       })
  //   : [];

  // Track when modal opens and handle specialty options loading
  useEffect(() => {
    // Reset ref when modal closes
    if (!isEditModalOpen) {
      hasSetLoadingForThisModalOpen.current = false;
      setLocalSpecialtyLoading(false);
      return;
    }

    // Modal is open
    if (specialtyOptions.length > 0) {
      // Options are available, clear local loading
      setLocalSpecialtyLoading(false);
      hasSetLoadingForThisModalOpen.current = false;
      return;
    }

    // Modal is open but options are empty
    // Keep loading true if parent is still loading
    if (isSpecialtyOptionsLoading) {
      setLocalSpecialtyLoading(true);
      hasSetLoadingForThisModalOpen.current = true;
      return;
    }

    // Parent finished loading but options are still empty
    // Only set loading once per modal open session, then wait a bit
    if (!hasSetLoadingForThisModalOpen.current) {
      hasSetLoadingForThisModalOpen.current = true;
      setLocalSpecialtyLoading(true);
      // Wait a bit for options to load after parent loading completes
      const timeout = setTimeout(() => {
        setLocalSpecialtyLoading(false);
      }, 3000); // Give 3 seconds after modal opens for options to load
      return () => clearTimeout(timeout);
    }
  }, [
    isEditModalOpen,
    specialtyOptions.length,
    isSpecialtyOptionsLoading,
  ]);

  // Keep fieldId in sync with resolved Daily-Alert field
  useEffect(() => {
    setFieldId(dailyAlertFieldId);
  }, [dailyAlertFieldId]);

  // If modal is open and field id is still missing, trigger one refresh of custom fields
  useEffect(() => {
    if (
      isEditModalOpen &&
      !dailyAlertFieldId &&
      !isFieldIdLoading &&
      !requestedFieldRefreshRef.current
    ) {
      requestedFieldRefreshRef.current = true;
      refreshCustomFields?.();
    }
  }, [
    isEditModalOpen,
    dailyAlertFieldId,
    isFieldIdLoading,
    refreshCustomFields,
  ]);

  // Handle case where custom fields failed to load
  // Note: With caching, this is less critical but we still log for debugging
  useEffect(() => {
    if (customFieldsError && !isFieldIdLoading) {
      console.warn(
        'Custom fields failed to load for DailyAlertsCard:',
        customFieldsError,
      );
    }
  }, [customFieldsError, isFieldIdLoading]);

  useEffect(() => {
    // Use provider data directly from props instead of making individual API calls
    if (provider) {
      const normalized = normalizeAlerts(
        provider?.customFields?.['Daily-Alert'],
      );
      setAlerts(normalized);
    } else if (practice) {
      const normalized = normalizeAlerts(
        practice?.customFields?.['Daily-Alert'],
      );
      setAlerts(normalized);
    }
  }, [practice, provider]);

  const handleEditAlert = useCallback((alert: CustomFieldValue) => {
    setEditingAlert(alert);
    setTempAlert(alert.value);
    setStartDate(alert.startDate);
    setEndDate(alert.endDate);
    setNeverExpire(alert.endDate === null);

    // Handle practice association
    const relatedPracticeId = alert.metadata?.relatedPracticeId;
    if (relatedPracticeId && typeof relatedPracticeId === 'string') {
      setAssociateWithPractice(true);
      setSelectedPracticeId(relatedPracticeId);
    } else {
      setAssociateWithPractice(false);
      setSelectedPracticeId('');
    }

    // Load categories from metadata
    const categories = alert.metadata?.categories;
    if (Array.isArray(categories)) {
      // Filter to only include string values
      const stringCategories = categories.filter(
        (cat): cat is string => typeof cat === 'string',
      );
      setSelectedCategories(stringCategories);
    } else {
      setSelectedCategories([]);
    }

    // Load transfer outcomes from metadata
    const loadedTransferOutcomes = getTransferOutcomes(
      alert.metadata,
    );
    if (loadedTransferOutcomes) {
      setTransferOutcomes(loadedTransferOutcomes);
    } else {
      // Default to transfer for all vectors
      setTransferOutcomes(createDefaultTransferOutcomes());
    }

    // Load comment log from metadata
    const loadedCommentLog = getCommentLog(alert.metadata);
    setCommentLog(loadedCommentLog);
    // Clear current comment when editing
    setComment('');

    setIsEditModalOpen(true);
  }, []);

  // Deep linking support: Check for alertId in URL query params
  // Use shared coordinator to prevent duplicate processing across components
  useEffect(() => {
    const alertIdFromUrl = searchParams.get('alertId');

    // Reset coordinator state if alertId is removed from URL
    if (!alertIdFromUrl) {
      clearProcessedAlertId(alertIdFromUrl);
      return;
    }

    // Skip if already processed by any component instance
    if (isAlertIdProcessed(alertIdFromUrl)) {
      return;
    }

    // We need an entity to process the deep link
    const hasEntity = practice || provider;
    if (!hasEntity) {
      // No entity yet, wait for next render
      return;
    }

    // Wait for permissions to be ready before processing
    // This prevents false "Access Denied" toasts when auth is still initializing
    if (!permissionsReady) {
      // Permissions not ready yet, wait for next render
      return;
    }

    // Check if customFields are available (even if null/empty, that's fine - means no alerts/notes)
    const hasCustomFields =
      practice?.customFields !== undefined ||
      provider?.customFields !== undefined;

    // If customFields not available yet, wait a bit for data to load
    // But set a timeout to prevent infinite waiting if API calls fail
    if (!hasCustomFields) {
      // Set a timeout to process after 2 seconds if data doesn't load
      setProcessingTimeout(
        alertIdFromUrl,
        () => {
          // After timeout, mark as processed and show "not found" toast
          if (!isAlertIdProcessed(alertIdFromUrl)) {
            markAlertIdAsProcessed(alertIdFromUrl);
            toast({
              title: 'Content Not Found',
              description:
                'Alert / Note you are trying to find might be expired.',
              status: 'warning',
              duration: 5000,
              isClosable: true,
            });
            searchParams.delete('alertId');
            setSearchParams(searchParams, { replace: true });
          }
        },
        2000,
      );

      return;
    }

    // Clear any pending timeout since we're processing now
    clearProcessingTimeout(alertIdFromUrl);

    // Get alerts directly from props to ensure we check against latest data
    const rawAlerts = provider
      ? provider?.customFields?.['Daily-Alert']
      : practice?.customFields?.['Daily-Alert'];

    // Use component's normalize function
    const currentAlerts = normalizeAlerts(rawAlerts);

    // Find the alert by ID
    const alertToOpen = currentAlerts.find(
      (alert) => alert.id === alertIdFromUrl,
    );

    if (alertToOpen) {
      // Found an alert - mark as processed and handle it
      markAlertIdAsProcessed(alertIdFromUrl);

      // Check RBAC permissions
      const hasPermission =
        canEditDailyNotes() || canEditOfficeAlerts() || isAdmin();

      if (hasPermission) {
        // Open edit modal with the alert
        handleEditAlert(alertToOpen);
        // Clean up query param
        searchParams.delete('alertId');
        setSearchParams(searchParams, { replace: true });
      } else {
        // Show error toast and redirect
        toast({
          title: 'Access Denied',
          description:
            "You don't have access to view or edit alerts/notes. Only admins and alert editors can access them.",
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        // Clean up query param
        searchParams.delete('alertId');
        setSearchParams(searchParams, { replace: true });
      }
    } else {
      // Alert not found, check notes as well
      const rawNotes = provider
        ? provider?.customFields?.['Office-Notes']
        : practice?.customFields?.['Office-Notes'];

      // Normalize notes using the same pattern as alerts
      const normalizedNotes = rawNotes
        ? (Array.isArray(rawNotes) ? rawNotes : [rawNotes]).filter(
            (note) => note && note.id,
          )
        : [];

      const noteToOpen = normalizedNotes.find(
        (note) => note.id === alertIdFromUrl,
      );

      if (!noteToOpen) {
        // Not found in either alerts or notes -> Expired
        // Mark as processed and show toast
        markAlertIdAsProcessed(alertIdFromUrl);
        toast({
          title: 'Content Not Found',
          description:
            'Alert / Note you are trying to find might be expired.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        // Clean up query param
        searchParams.delete('alertId');
        setSearchParams(searchParams, { replace: true });
      }
      // If note is found, DON'T mark as processed - let OfficeNotesCard handle it
    }

    // Cleanup: Clear timeout if component unmounts or alertId changes
    return () => {
      if (alertIdFromUrl) {
        clearProcessingTimeout(alertIdFromUrl);
      }
    };
  }, [
    searchParams,
    practice,
    provider,
    permissionsReady,
    canEditDailyNotes,
    canEditOfficeAlerts,
    isAdmin,
    handleEditAlert,
    toast,
    setSearchParams,
  ]);

  const handleAddNewAlert = useCallback(() => {
    setEditingAlert(null);
    setTempAlert('');
    setStartDate(null);
    setEndDate(null);
    setNeverExpire(false);
    setAssociateWithPractice(false);
    setSelectedPracticeId('');
    setSelectedCategories([]);
    // Reset transfer outcomes to defaults (transfer for all vectors)
    setTransferOutcomes(createDefaultTransferOutcomes());
    setComment('');
    setCommentLog([]);
    setIsEditModalOpen(true);
  }, []);

  const handleSaveAlert = async () => {
    // Validation: Note text cannot be empty (check visible text content, not just HTML)
    const visibleText = getVisibleText(tempAlert);
    if (!visibleText || visibleText.trim().length === 0) {
      toast({
        title: 'Note text is required.',
        description: 'Please enter the alert message before saving.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Validation: Start date cannot be empty
    if (!startDate) {
      toast({
        title: 'Start Date is required.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Validation: Start date can be in the past, but end date must be today or in the future
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
    // Keep variable for potential future checks; avoids unused var lint by simple reference
    const startDateObj = new Date(startDate);
    void startDateObj;

    // Check if end date is in the past (only for new items)
    if (!neverExpire && endDate && !editingAlert) {
      const endDateObj = new Date(endDate);
      if (endDateObj < today) {
        toast({
          title: 'Invalid End Date',
          description:
            'End date cannot be in the past for new alerts.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
    }

    // Validation: End date cannot be empty unless "Never Expire" is checked
    if (!neverExpire && !endDate) {
      toast({
        title:
          'End Date is required unless "Never Expire" is selected.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Validation: If practice association is checked, practice must be selected
    if (
      provider && // Only validate for providers
      associateWithPractice &&
      (!selectedPracticeId || selectedPracticeId.trim() === '')
    ) {
      toast({
        title: 'Practice Selection Required',
        description:
          'Please select a practice when "Associate with Practice" is checked.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Validation: Date logic and user feedback
    if (!neverExpire && endDate) {
      const endDateObj = new Date(endDate);

      // Note: Start dates can be in the past, only end dates are validated

      // Enforce End Date on or after Start Date (allow same-day alerts)
      const startVsEndStart = new Date(startDate);
      if (endDateObj < startVsEndStart) {
        toast({
          title: 'Invalid Date Range',
          description: 'End Date must be on or after the Start Date.',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
        return;
      }

      // Allow endDate to be today, including start=end=today

      // Note: Start dates can be in the past, only end dates are validated
    }

    // Use fieldId from the alert itself if updating, or state fieldId for new alerts
    const alertFieldId = editingAlert?.fieldId || dailyAlertFieldId;
    if (!alertFieldId) {
      console.error('Field ID is missing. Cannot save alert.', {
        editingAlert: editingAlert?.id,
        alertFieldId: editingAlert?.fieldId,
        stateFieldId: fieldId,
      });
      toast({
        title: 'Field ID is missing. Cannot save alert.',
        description: 'Please refresh the page and try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Prepare transfer outcomes with alwaysTransfer set to true
    const transferOutcomesToSave = {
      ...transferOutcomes,
      alwaysTransfer: true, // Set to true when "Save Alert" is clicked
    };

    const entityId = provider ? provider.id : practice?.id || '';
    const entityType = provider ? 'provider' : 'practice';

    const updatedCommentLogEntries = (() => {
      const userInfo = getUserInfoFromToken();
      if (!userInfo) {
        return commentLog;
      }
      const action = editingAlert ? 'updated' : 'created';
      const newEntry = createCommentLogEntry(action, {
        userId: userInfo.id,
        userName: userInfo.name,
        userEmail: userInfo.email,
        permissionGroup: userInfo.permissionGroup,
      });
      return [...commentLog, newEntry];
    })();

    const existingMetadata = {
      ...(editingAlert?.metadata || {}),
    } as Record<string, unknown>;

    const sanitizedMetadata: Record<string, unknown> = {
      ...existingMetadata,
      htmlContent: tempAlert,
      categories: selectedCategories,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      transferOutcomes: transferOutcomesToSave as any,
      commentLog: updatedCommentLogEntries,
    };

    if (neverExpire) {
      sanitizedMetadata.neverExpire = 'true';
    } else {
      delete sanitizedMetadata.neverExpire;
    }

    if (
      provider &&
      associateWithPractice &&
      selectedPracticeId &&
      selectedPracticeId.trim() !== ''
    ) {
      sanitizedMetadata.relatedPracticeId = selectedPracticeId;
      sanitizedMetadata.relatedPracticeName =
        provider?.relatedPractices?.find(
          (practice) => practice.id === selectedPracticeId,
        )?.name || 'Unknown Practice';
    } else {
      delete sanitizedMetadata.relatedPracticeId;
      delete sanitizedMetadata.relatedPracticeName;
    }

    delete sanitizedMetadata.approvalStatus;
    delete sanitizedMetadata.approvedAt;
    delete sanitizedMetadata.approvedBy;
    delete sanitizedMetadata.submittedForApproval;
    delete sanitizedMetadata.submittedAt;

    const newAlert: CustomFieldValue = {
      fieldId: alertFieldId,
      rowId: entityId,
      value: tempAlert,
      startDate,
      endDate: neverExpire ? null : endDate,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      metadata: sanitizedMetadata as any,
      id: editingAlert?.id ?? undefined,
    };

    setCommentLog(updatedCommentLogEntries);

    const updatedAlerts = editingAlert
      ? alerts.map((alert) =>
          alert.id === editingAlert.id
            ? { ...alert, ...newAlert } // Update the existing alert
            : alert,
        )
      : [...alerts, newAlert];

    if (editingAlert) {
      setEditingAlert({ ...editingAlert, ...newAlert });
    }

    // Update local state immediately for instant UI feedback
    setAlerts(updatedAlerts);

    // Save alerts and get updated alerts with IDs from backend
    const savedAlertsWithIds = await saveAlerts(updatedAlerts);
    analytics.trackEvent(editingAlert ? 'update' : 'create', 'daily_alert');

    // Find the saved alert by matching value and startDate (for new alerts) or by ID (for existing alerts)
    const savedAlert = savedAlertsWithIds.find(
      (alert) =>
        (editingAlert?.id && alert.id === editingAlert.id) ||
        (!editingAlert?.id &&
          alert.value === tempAlert &&
          alert.startDate === startDate),
    );
    const alertIdAfterSave = savedAlert?.id || editingAlert?.id;

    // Send notification to Denim users
    try {
      await notifyDenimOnSave({
        alertId: alertIdAfterSave,
        entityType,
        entityId,
        link: generateAlertLink(
          entityType,
          entityId,
          alertIdAfterSave,
        ),
        entityName: getEntityName(),
      });
    } catch (error) {
      console.error('Failed to send notification:', error);
      // Don't block the save operation
    }

    // Clear comment after save
    setComment('');

    setIsUpdating(false);
    setIsEditModalOpen(false);
  };

  const handleAddComment = async () => {
    if (!comment.trim()) {
      toast({
        title: 'Comment Required',
        description:
          'Please enter a comment before adding it to the log.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!editingAlert?.id) {
      toast({
        title: 'Save Required',
        description:
          'Please save the alert first before adding comments.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    if (!editingAlert) {
      toast({
        title: 'Error',
        description:
          'No alert selected. Please save the alert first.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsAddingComment(true);

    try {
      const userInfo = getUserInfoFromToken();
      if (!userInfo) {
        toast({
          title: 'Error',
          description:
            'User information is missing. Please refresh and try again.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      // Add comment entry to comment log
      const newCommentEntry = createCommentLogEntry(
        'comment_added',
        {
          userId: userInfo.id,
          userName: userInfo.name,
          userEmail: userInfo.email,
          permissionGroup: userInfo.permissionGroup,
        },
        comment.trim(),
      );

      const updatedCommentLog = [...commentLog, newCommentEntry];

      // Update the alert with new comment log
      const updatedAlert: CustomFieldValue = {
        ...editingAlert,
        metadata: {
          ...editingAlert.metadata,
          commentLog: updatedCommentLog,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any,
      };

      const updatedAlerts = alerts.map((alert) =>
        alert.id === editingAlert.id ? updatedAlert : alert,
      );

      // Save the updated alert
      await saveAlerts(updatedAlerts, false);

      // Update local state
      setCommentLog(updatedCommentLog);
      setComment(''); // Clear comment after adding

      toast({
        title: 'Comment Added Successfully',
        description: 'Your comment has been added to the audit log.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Failed to add comment:', error);
      toast({
        title: 'Failed to Add Comment',
        description:
          'An error occurred while adding the comment. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsAddingComment(false);
    }
  };

  const handleSubmitForApproval = async () => {
    if (!editingAlert?.id) {
      toast({
        title: 'Save Required First',
        description:
          'Please save the alert first before submitting for approval. The alert must exist before it can be submitted.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (!comment.trim()) {
      toast({
        title: 'Comment Required',
        description:
          'Please enter a comment explaining the transfer outcomes before submitting for approval. This helps CHS reviewers understand your decisions.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setIsSubmittingForApproval(true);

    try {
      const entityId = provider ? provider.id : practice?.id || '';
      const entityType = provider ? 'provider' : 'practice';

      // Update the alert with pending approval status
      if (editingAlert) {
        // Use fieldId from the alert itself, or fall back to state fieldId
        const alertFieldId =
          editingAlert.fieldId || dailyAlertFieldId;
        if (!alertFieldId) {
          console.error(
            'Field ID is missing. Cannot submit for approval.',
            {
              editingAlert: editingAlert.id,
              alertFieldId: editingAlert.fieldId,
              stateFieldId: fieldId,
            },
          );
          toast({
            title: 'Error',
            description:
              'Field ID is missing. Please refresh and try again.',
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
          return;
        }
        const userInfo = getUserInfoFromToken();
        const newCommentLogEntry = userInfo
          ? createCommentLogEntry(
              'submitted_for_approval',
              {
                userId: userInfo.id,
                userName: userInfo.name,
                userEmail: userInfo.email,
                permissionGroup: userInfo.permissionGroup,
              },
              comment.trim() || undefined,
            )
          : null;

        // Prepare transfer outcomes with alwaysTransfer set to true when submitting for approval
        const transferOutcomesToSubmit = {
          ...transferOutcomes,
          alwaysTransfer: true, // Set to true when "Submit for Approval" is clicked
        };

        const updatedAlert: CustomFieldValue = {
          ...editingAlert,
          metadata: {
            ...editingAlert.metadata,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            transferOutcomes: transferOutcomesToSubmit as any,
            commentLog: newCommentLogEntry
              ? [...commentLog, newCommentLogEntry]
              : commentLog,
            approvalStatus: 'pending',
            submittedForApproval: true,
            submittedAt: new Date().toISOString(),
            submittedBy: '', // Backend will populate this
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } as any,
        };

        const updatedAlerts = alerts.map((alert) =>
          alert.id === editingAlert.id ? updatedAlert : alert,
        );

        await saveAlerts(updatedAlerts, false);

        // Send notification to CHS users
        await notifyCHSOnSubmission({
          alertId: editingAlert.id,
          entityType,
          entityId,
          link: generateAlertLink(
            entityType,
            entityId,
            editingAlert.id,
          ),
          entityName: getEntityName(),
        });

        toast({
          title: 'Submitted for Approval',
          description:
            'Transfer outcomes have been submitted for CHS approval. CHS PAC managers will be notified and can review your submission.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });

        // Clear comment after submission
        setComment('');

        setEditingAlert(updatedAlert);

        setIsEditModalOpen(false);
      }
    } catch (error) {
      console.error('Failed to submit for approval:', error);
      toast({
        title: 'Failed to Submit for Approval',
        description:
          'An error occurred while submitting for approval. Please check your connection and try again. If the problem persists, contact support.',
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setIsSubmittingForApproval(false);
    }
  };

  const handleApproveTransferOutcomes = async () => {
    if (!editingAlert) {
      toast({
        title: 'No Alert Selected',
        description:
          'No alert is currently selected for approval. Please select an alert first.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!editingAlert.id) {
      toast({
        title: 'Save Required First',
        description:
          'Please save the alert first before approving transfer outcomes.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    const editingMetadata =
      (editingAlert.metadata as
        | TransferOutcomesMetadata
        | undefined) || undefined;

    if (!editingMetadata?.submittedForApproval) {
      toast({
        title: 'Submit for Approval First',
        description:
          'Submit the transfer outcomes for approval before CHS can approve them.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Use fieldId from the alert itself, or fall back to state fieldId
    const alertFieldId = editingAlert.fieldId || dailyAlertFieldId;
    if (!alertFieldId) {
      console.error(
        'Field ID is missing. Cannot approve transfer outcomes.',
        {
          editingAlert: editingAlert.id,
          alertFieldId: editingAlert.fieldId,
          stateFieldId: fieldId,
        },
      );
      toast({
        title: 'Error',
        description:
          'Field ID is missing. Please refresh and try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsApproving(true);

    try {
      console.log('Approving transfer outcomes:', {
        editingAlert: editingAlert?.id,
        fieldId: alertFieldId,
        transferOutcomes,
        commentLog: commentLog.length,
      });

      const entityId = provider ? provider.id : practice?.id || '';
      const entityType = provider ? 'provider' : 'practice';

      // Update transfer outcomes: set alwaysTransfer to false and approval status to approved
      const approvedTransferOutcomes = {
        ...transferOutcomes,
        alwaysTransfer: false, // Unset when "Approve Transfer Outcomes" is clicked
      };

      const userInfo = getUserInfoFromToken();
      const newCommentLogEntry = userInfo
        ? createCommentLogEntry(
            'approved',
            {
              userId: userInfo.id,
              userName: userInfo.name,
              userEmail: userInfo.email,
              permissionGroup: userInfo.permissionGroup,
            },
            comment.trim() || undefined,
          )
        : null;

      const updatedAlert: CustomFieldValue = {
        ...editingAlert,
        metadata: {
          ...editingAlert.metadata,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          transferOutcomes: approvedTransferOutcomes as any,
          commentLog: newCommentLogEntry
            ? [...commentLog, newCommentLogEntry]
            : commentLog,
          approvalStatus: 'approved',
          approvedAt: new Date().toISOString(),
          submittedForApproval: false,
          // Store full user info object in metadata (for comment log and audit trail)
          approvedBy: userInfo
            ? {
                userId: userInfo.id,
                userName: userInfo.name,
                userEmail: userInfo.email,
                permissionGroup: userInfo.permissionGroup,
              }
            : undefined,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any,
      };

      const updatedAlerts = alerts.map((alert) =>
        alert.id === editingAlert.id ? updatedAlert : alert,
      );

      await saveAlerts(updatedAlerts, false);

      // Send notification to Denim users
      await notifyDenimOnApproval({
        alertId: editingAlert.id,
        entityType,
        entityId,
        link: generateAlertLink(
          entityType,
          entityId,
          editingAlert.id,
        ),
        entityName: getEntityName(),
      });

      // Clear comment after approval
      setComment('');

      setEditingAlert(updatedAlert);

      toast({
        title: 'Transfer Outcomes Approved',
        description:
          'Transfer outcomes have been approved and are now live. Denim users have been notified of the approval.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      setIsEditModalOpen(false);
    } catch (error) {
      console.error('Failed to approve transfer outcomes:', error);
      toast({
        title: 'Failed to Approve Transfer Outcomes',
        description:
          'An error occurred while approving transfer outcomes. Please check your connection and try again. If the problem persists, contact support.',
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setIsApproving(false);
    }
  };

  const handleDeleteAlert = async () => {
    if (!alertToDelete) return;

    // Set endDate to day before yesterday for proper deletion across all timezones
    const dayBeforeYesterday = new Date();
    dayBeforeYesterday.setDate(dayBeforeYesterday.getDate() - 2);
    // Format date safely to avoid timezone issues
    const year = dayBeforeYesterday.getFullYear();
    const month = String(dayBeforeYesterday.getMonth() + 1).padStart(
      2,
      '0',
    );
    const day = String(dayBeforeYesterday.getDate()).padStart(2, '0');
    const ymd = `${year}-${month}-${day}`;

    const updatedAlerts = alerts.map((alert) =>
      alert.id === alertToDelete.id
        ? {
            ...alert,
            endDate: ymd,
          }
        : alert,
    );

    try {
      setIsDeleting(true);
      await saveAlerts(updatedAlerts, false); // Don't show success toast from saveAlerts
      analytics.trackEvent('delete', 'daily_alert');

      // Only update UI after successful API call - directly remove the deleted alert
      setAlerts((prevAlerts) =>
        prevAlerts.filter((alert) => alert.id !== alertToDelete?.id),
      );

      // Show success feedback only after successful API call
      toast({
        title: 'Alert Deleted',
        description: 'Daily alert has been deleted successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error deleting alert:', error);
      toast({
        title: 'Failed to delete alert',
        description:
          'An error occurred while deleting the alert. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDeleting(false);
      onDeleteClose();
    }
  };

  const saveAlerts = async (
    updatedAlerts: CustomFieldValue[],
    showSuccessToast: boolean = true,
  ): Promise<CustomFieldValue[]> => {
    setIsUpdating(true);

    try {
      // Filter out alerts with empty or whitespace-only values
      const validAlerts = updatedAlerts.filter(
        (alert) => alert.value && alert.value.trim().length > 0,
      );

      if (provider) {
        const cleanedProvider: Partial<Provider> = {
          id: provider.id,
          customFields: {
            'Daily-Alert': validAlerts.map((alert) => ({
              ...alert,
              id: alert.id ?? undefined, // Ensure id is included for edited alerts
            })),
          },
        };

        await updateProviderById(cleanedProvider.id, cleanedProvider);

        // Re-fetch provider to get updated alerts with IDs from backend
        const { getProviderById } =
          await import('../../services/providers/getProviderById');
        const { data } = await getProviderById(provider.id);
        const savedAlerts =
          data?.provider?.customFields?.['Daily-Alert'] || [];
        const alertsWithIds = Array.isArray(savedAlerts)
          ? savedAlerts
          : [];

        // Update local state with alerts that have IDs from backend
        setAlerts(alertsWithIds);

        // Show success feedback only if requested (not for deletions)
        if (showSuccessToast) {
          toast({
            title: 'Alert Saved',
            description: 'Daily alert saved successfully.',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
        }

        return alertsWithIds;
      } else if (practice) {
        const cleanedPractice: Partial<Practice> = {
          id: practice.id,
          customFields: {
            'Daily-Alert': validAlerts.map((alert) => ({
              ...alert,
              id: alert.id ?? undefined, // Ensure id is included for edited alerts
            })),
          } as Practice['customFields'],
        };

        await updatePracticeById(cleanedPractice.id, cleanedPractice);

        // Re-fetch practice to get updated alerts with IDs from backend
        const { getPracticeById } =
          await import('../../services/practices/getPracticeById');
        const { data } = await getPracticeById(practice.id);
        const savedAlerts =
          data?.practice?.customFields?.['Daily-Alert'] || [];
        const alertsWithIds = Array.isArray(savedAlerts)
          ? savedAlerts
          : [];

        // Update local state with alerts that have IDs from backend
        setAlerts(alertsWithIds);

        // Show success feedback only if requested (not for deletions)
        if (showSuccessToast) {
          toast({
            title: 'Alert Saved',
            description: 'Daily alert saved successfully.',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
        }

        return alertsWithIds;
      }

      setIsEditModalOpen(false);
      return validAlerts;
    } catch (error) {
      const isProvider = !!provider;
      const errorTitle = isProvider
        ? 'Failed to update provider alerts.'
        : 'Failed to update daily alerts.';

      // Only show error toast if showSuccessToast is true (for regular saves)
      // For deletions, let the calling function handle the error
      if (showSuccessToast) {
        toast({
          title: errorTitle,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }

      // Always re-throw the error so calling functions can handle it
      throw error;
    } finally {
      setIsUpdating(false);
    }
  };

  // Derive visible list defensively to avoid empty-card renders
  const visibleAlerts = Array.isArray(alerts)
    ? alerts.filter((a) => !!a && a.value && a.value.trim())
    : [];

  // Sort alerts by start date (most recent first)
  const sortedAlerts = visibleAlerts.sort(
    (a, b) =>
      new Date(b.startDate || '').getTime() -
      new Date(a.startDate || '').getTime(),
  );

  const hasAlerts = sortedAlerts.length > 0;
  const latestAlert = hasAlerts ? sortedAlerts[0] : undefined;
  const olderAlerts = hasAlerts ? sortedAlerts.slice(1) : [];

  const submittedForApproval = Boolean(
    (editingAlert?.metadata as TransferOutcomesMetadata | undefined)
      ?.submittedForApproval,
  );

  const approveDisabledReason = submittedForApproval
    ? undefined
    : 'Submit for approval before CHS can approve transfer outcomes.';

  return (
    <Box
      padding="0.5rem 0.625rem"
      backgroundColor="red.100"
      borderRadius="0.5rem"
      width={width ? width : '100%'}
      mt="0.25rem"
    >
      {isFieldIdLoading ? (
        <Flex align="center" justify="center">
          <Spinner size="md" color="yellow.800" />
          <Text ml="1rem">
            {provider
              ? 'Loading Provider Alerts...'
              : 'Loading Daily Alerts...'}
          </Text>
        </Flex>
      ) : (
        <Stack spacing={4}>
          {hasAlerts && latestAlert && (
            <Box
              key={latestAlert.id}
              padding="1rem"
              backgroundColor="red.50"
              border="1px solid"
              borderColor="red.200"
              borderRadius="0.75rem"
              display="flex"
              flexDirection="column"
              gap="0.75rem"
              minWidth={0}
              overflow="hidden"
              _hover={{
                boxShadow: 'md',
                borderColor: 'red.300',
              }}
              transition="all 0.2s ease-in-out"
            >
              {/* Header Row */}
              <Flex
                justify="flex-start"
                align="center"
                flexWrap="wrap"
                gap={2}
              >
                <HStack spacing={3} align="center">
                  <img
                    src={DailyAlertIcon}
                    alt={
                      provider
                        ? 'Provider Alert Icon'
                        : 'Daily Alert Icon'
                    }
                    style={{ width: '20px', height: '20px' }}
                  />
                  <Text
                    fontSize="1rem"
                    fontWeight="600"
                    color="gray.800"
                  >
                    {provider
                      ? 'Provider Alert'
                      : 'Practice Daily Alert'}
                  </Text>
                </HStack>

                {/* Actions - Will wrap to next line if needed */}
                <HStack
                  spacing={2}
                  minW="5rem"
                  justify="flex-end"
                  ml="auto"
                >
                  {canEditDailyNotes() ||
                  canEditOfficeAlerts() ||
                  isAdmin() ? (
                    <>
                      <IconButton
                        aria-label="Edit Alert"
                        icon={<EditIcon />}
                        size="sm"
                        onClick={() => handleEditAlert(latestAlert)}
                        backgroundColor="white"
                        _hover={{ backgroundColor: 'gray.100' }}
                      />
                      <IconButton
                        aria-label="Delete Alert"
                        icon={<DeleteIcon />}
                        size="sm"
                        onClick={() => {
                          setAlertToDelete(latestAlert);
                          onDeleteOpen();
                        }}
                        backgroundColor="white"
                        _hover={{ backgroundColor: 'gray.100' }}
                        isDisabled={isDeleting}
                      />
                    </>
                  ) : null}
                </HStack>
              </Flex>

              {/* Practice Association */}
              {latestAlert.metadata?.relatedPracticeId &&
                (() => {
                  const practiceId =
                    latestAlert?.metadata?.relatedPracticeId;
                  // Use relatedPracticeName from metadata if available, otherwise fall back to getPracticeNameById
                  const practiceName = String(
                    latestAlert.metadata?.relatedPracticeName ||
                      (practiceId
                        ? getPracticeNameById(practiceId)
                        : '') ||
                      '',
                  );

                  // Only show if we have a valid practice name (not "Unknown Practice")
                  if (
                    !practiceId ||
                    practiceName === 'Unknown Practice'
                  ) {
                    return null;
                  }

                  return (
                    <Box>
                      <Text
                        fontSize="0.875rem"
                        color="red.600"
                        fontWeight="500"
                      >
                        Related to:{' '}
                        <span
                          style={{
                            textDecoration: 'underline',
                            cursor: 'pointer',
                          }}
                          onClick={() =>
                            navigate(`/practices/${practiceId}`)
                          }
                          tabIndex={0}
                          role="link"
                          aria-label={`Go to practice ${practiceName}`}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              navigate(`/practices/${practiceId}`);
                            }
                          }}
                        >
                          {practiceName}
                        </span>
                      </Text>
                    </Box>
                  );
                })()}

              {/* Categories */}
              {latestAlert.metadata?.categories &&
                Array.isArray(latestAlert.metadata.categories) &&
                latestAlert.metadata.categories.length > 0 && (
                  <Flex gap={2} flexWrap="wrap">
                    {latestAlert.metadata.categories
                      .filter(
                        (cat): cat is string =>
                          typeof cat === 'string',
                      )
                      .map((category, index) => (
                        <Tooltip
                          key={index}
                          label={
                            CATEGORY_LEGEND[
                              category as keyof typeof CATEGORY_LEGEND
                            ]?.definition || category
                          }
                          hasArrow
                          placement="top"
                          bg="gray.800"
                          color="white"
                          fontSize="sm"
                          px={3}
                          py={2}
                          borderRadius="md"
                          maxW="300px"
                        >
                          <Badge
                            colorScheme={getCategoryColor(category)}
                            fontSize="xs"
                            px={2}
                            py={1}
                            borderRadius="md"
                            cursor="help"
                          >
                            {CATEGORY_LEGEND[
                              category as keyof typeof CATEGORY_LEGEND
                            ]?.name || category.replace(/_/g, ' ')}
                          </Badge>
                        </Tooltip>
                      ))}
                  </Flex>
                )}

              {/* Transfer Outcomes Status Badges - Only visible to users with permissions */}
              {(canEditDailyNotes() ||
                canEditOfficeAlerts() ||
                isAdmin()) &&
                (() => {
                  const transferOutcomesData = getTransferOutcomes(
                    latestAlert.metadata,
                  );
                  const approvalStatus =
                    latestAlert.metadata?.approvalStatus;
                  const alwaysTransfer =
                    transferOutcomesData?.alwaysTransfer;

                  if (!transferOutcomesData && !approvalStatus) {
                    return null;
                  }

                  return (
                    <Flex gap={2} flexWrap="wrap" alignItems="center">
                      {/* Approval Status Badge */}
                      {approvalStatus && (
                        <Tooltip
                          label={
                            approvalStatus === 'pending'
                              ? 'Awaiting CHS approval'
                              : approvalStatus === 'approved'
                                ? 'Transfer outcomes approved and live'
                                : 'Draft - not yet submitted'
                          }
                          hasArrow
                          placement="top"
                          bg="gray.800"
                          color="white"
                          fontSize="sm"
                          px={3}
                          py={2}
                          borderRadius="md"
                        >
                          <Badge
                            colorScheme={
                              approvalStatus === 'approved'
                                ? 'green'
                                : approvalStatus === 'pending'
                                  ? 'orange'
                                  : 'gray'
                            }
                            fontSize="xs"
                            px={2}
                            py={1}
                            borderRadius="md"
                            cursor="help"
                          >
                            {approvalStatus === 'approved'
                              ? 'Approved'
                              : approvalStatus === 'pending'
                                ? 'Pending Approval'
                                : 'Draft'}
                          </Badge>
                        </Tooltip>
                      )}

                      {/* Always Transfer Badge - Only show if true */}
                      {alwaysTransfer === true && (
                        <Tooltip
                          label="Bot will always transfer until outcomes are approved"
                          hasArrow
                          placement="top"
                          bg="gray.800"
                          color="white"
                          fontSize="sm"
                          px={3}
                          py={2}
                          borderRadius="md"
                        >
                          <Badge
                            colorScheme="yellow"
                            fontSize="xs"
                            px={2}
                            py={1}
                            borderRadius="md"
                            cursor="help"
                          >
                            Always Transfer
                          </Badge>
                        </Tooltip>
                      )}
                    </Flex>
                  );
                })()}

              {/* Note Preview */}
              <NotePreview
                content={
                  typeof latestAlert.metadata?.htmlContent ===
                  'string'
                    ? latestAlert.metadata.htmlContent
                    : latestAlert.value
                }
              />
            </Box>
          )}

          {olderAlerts.length > 0 && (
            <Collapse in={!isCollapsed}>
              {olderAlerts.map((alert) => (
                <Box
                  key={alert.id}
                  padding="1rem"
                  backgroundColor="red.50"
                  border="1px solid"
                  borderColor="red.200"
                  borderRadius="0.75rem"
                  display="flex"
                  flexDirection="column"
                  gap="0.75rem"
                  mt="0.5rem"
                  minWidth={0}
                  overflow="hidden"
                  _hover={{
                    boxShadow: 'md',
                    borderColor: 'red.300',
                  }}
                  transition="all 0.2s ease-in-out"
                >
                  {/* Header Row */}
                  <Flex
                    justify="flex-start"
                    align="center"
                    flexWrap="wrap"
                    gap={2}
                  >
                    <HStack spacing={3} align="center">
                      <img
                        src={DailyAlertIcon}
                        alt={
                          provider
                            ? 'Provider Alert Icon'
                            : 'Daily Alert Icon'
                        }
                        style={{ width: '20px', height: '20px' }}
                      />
                      <Text
                        fontSize="1rem"
                        fontWeight="600"
                        color="gray.800"
                      >
                        {provider
                          ? 'Provider Alert'
                          : 'Practice Daily Alert'}
                      </Text>
                    </HStack>

                    {/* Actions - Will wrap to next line if needed */}
                    <HStack
                      spacing={2}
                      minW="5rem"
                      justify="flex-end"
                      ml="auto"
                    >
                      {canEditDailyNotes() ||
                      canEditOfficeAlerts() ||
                      isAdmin() ? (
                        <>
                          <IconButton
                            aria-label="Edit Alert"
                            icon={<EditIcon />}
                            size="sm"
                            onClick={() => handleEditAlert(alert)}
                            backgroundColor="white"
                            _hover={{ backgroundColor: 'gray.100' }}
                          />
                          <IconButton
                            aria-label="Delete Alert"
                            icon={<DeleteIcon />}
                            size="sm"
                            onClick={() => {
                              setAlertToDelete(alert);
                              onDeleteOpen();
                            }}
                            backgroundColor="white"
                            _hover={{ backgroundColor: 'gray.100' }}
                            isDisabled={isDeleting}
                          />
                        </>
                      ) : null}
                    </HStack>
                  </Flex>

                  {/* Practice Association */}
                  {alert.metadata?.relatedPracticeId &&
                    (() => {
                      const practiceId =
                        alert.metadata?.relatedPracticeId;
                      // Use relatedPracticeName from metadata if available, otherwise fall back to getPracticeNameById
                      const practiceName = String(
                        alert.metadata?.relatedPracticeName ||
                          (practiceId
                            ? getPracticeNameById(practiceId)
                            : '') ||
                          '',
                      );

                      // Only show if we have a valid practice name (not "Unknown Practice")
                      if (
                        !practiceId ||
                        practiceName === 'Unknown Practice'
                      ) {
                        return null;
                      }

                      return (
                        <Box>
                          <Text
                            fontSize="0.875rem"
                            color="red.600"
                            fontWeight="500"
                          >
                            Related to:{' '}
                            <span
                              style={{
                                textDecoration: 'underline',
                                cursor: 'pointer',
                              }}
                              onClick={() =>
                                navigate(`/practices/${practiceId}`)
                              }
                              tabIndex={0}
                              role="link"
                              aria-label={`Go to practice ${practiceName}`}
                              onKeyDown={(e) => {
                                if (
                                  e.key === 'Enter' ||
                                  e.key === ' '
                                ) {
                                  navigate(
                                    `/practices/${practiceId}`,
                                  );
                                }
                              }}
                            >
                              {practiceName}
                            </span>
                          </Text>
                        </Box>
                      );
                    })()}

                  {/* Categories */}
                  {alert.metadata?.categories &&
                    Array.isArray(alert.metadata.categories) &&
                    alert.metadata.categories.length > 0 && (
                      <Flex gap={2} flexWrap="wrap">
                        {alert.metadata.categories
                          .filter(
                            (cat): cat is string =>
                              typeof cat === 'string',
                          )
                          .map((category, index) => (
                            <Tooltip
                              key={index}
                              label={
                                CATEGORY_LEGEND[
                                  category as keyof typeof CATEGORY_LEGEND
                                ]?.definition || category
                              }
                              hasArrow
                              placement="top"
                              bg="gray.800"
                              color="white"
                              fontSize="sm"
                              px={3}
                              py={2}
                              borderRadius="md"
                              maxW="300px"
                            >
                              <Badge
                                colorScheme={getCategoryColor(
                                  category,
                                )}
                                fontSize="xs"
                                px={2}
                                py={1}
                                borderRadius="md"
                                cursor="help"
                              >
                                {CATEGORY_LEGEND[
                                  category as keyof typeof CATEGORY_LEGEND
                                ]?.name ||
                                  category.replace(/_/g, ' ')}
                              </Badge>
                            </Tooltip>
                          ))}
                      </Flex>
                    )}

                  {/* Transfer Outcomes Status Badges - Only visible to users with permissions */}
                  {(canEditDailyNotes() ||
                    canEditOfficeAlerts() ||
                    isAdmin()) &&
                    (() => {
                      const transferOutcomesData =
                        getTransferOutcomes(alert.metadata);
                      const approvalStatus =
                        alert.metadata?.approvalStatus;
                      const alwaysTransfer =
                        transferOutcomesData?.alwaysTransfer;

                      if (!transferOutcomesData && !approvalStatus) {
                        return null;
                      }

                      return (
                        <Flex
                          gap={2}
                          flexWrap="wrap"
                          alignItems="center"
                        >
                          {/* Approval Status Badge */}
                          {approvalStatus && (
                            <Tooltip
                              label={
                                approvalStatus === 'pending'
                                  ? 'Awaiting CHS approval'
                                  : approvalStatus === 'approved'
                                    ? 'Transfer outcomes approved and live'
                                    : 'Draft - not yet submitted'
                              }
                              hasArrow
                              placement="top"
                              bg="gray.800"
                              color="white"
                              fontSize="sm"
                              px={3}
                              py={2}
                              borderRadius="md"
                            >
                              <Badge
                                colorScheme={
                                  approvalStatus === 'approved'
                                    ? 'green'
                                    : approvalStatus === 'pending'
                                      ? 'orange'
                                      : 'gray'
                                }
                                fontSize="xs"
                                px={2}
                                py={1}
                                borderRadius="md"
                                cursor="help"
                              >
                                {approvalStatus === 'approved'
                                  ? 'Approved'
                                  : approvalStatus === 'pending'
                                    ? 'Pending Approval'
                                    : 'Draft'}
                              </Badge>
                            </Tooltip>
                          )}

                          {/* Always Transfer Badge - Only show if true */}
                          {alwaysTransfer === true && (
                            <Tooltip
                              label="Bot will always transfer until outcomes are approved"
                              hasArrow
                              placement="top"
                              bg="gray.800"
                              color="white"
                              fontSize="sm"
                              px={3}
                              py={2}
                              borderRadius="md"
                            >
                              <Badge
                                colorScheme="yellow"
                                fontSize="xs"
                                px={2}
                                py={1}
                                borderRadius="md"
                                cursor="help"
                              >
                                Always Transfer
                              </Badge>
                            </Tooltip>
                          )}
                        </Flex>
                      );
                    })()}

                  {/* Note Preview */}
                  <NotePreview
                    content={
                      typeof alert.metadata?.htmlContent === 'string'
                        ? alert.metadata.htmlContent
                        : alert.value
                    }
                  />
                </Box>
              ))}
            </Collapse>
          )}

          {/* Add Alert and Show More buttons */}
          <Flex
            justify="flex-start"
            align="center"
            flexWrap="wrap"
            gap={2}
          >
            {/* Show "No Data" message when no alerts exist */}
            {!hasAlerts && (
              <Text
                fontSize="0.875rem"
                color="gray.500"
                fontStyle="italic"
                textAlign="center"
                width="100%"
                py="1rem"
              >
                {practice
                  ? 'No Practice Daily Alerts'
                  : 'No Provider Alerts'}
              </Text>
            )}

            {/* Always show Add button for users with permissions */}
            {(canEditDailyNotes() ||
              canEditOfficeAlerts() ||
              isAdmin()) && (
              <>
                {passedCustomFieldsError ? (
                  <Flex align="center" gap={2} px={4}>
                    <Text
                      fontSize="0.875rem"
                      color="red.500"
                      fontWeight="500"
                    >
                      Failed to initialize Alerts.
                    </Text>
                    <Button
                      size="xs"
                      colorScheme="blue"
                      variant="ghost"
                      onClick={() => refreshCustomFields?.()}
                    >
                      Retry
                    </Button>
                  </Flex>
                ) : passedCustomFieldsLoading ||
                  (passedCustomFields && !dailyAlertFieldId) ? (
                  <Flex align="center" gap={2} px={4}>
                    <Spinner size="sm" color="red.500" />
                    <Text fontSize="0.875rem" color="gray.600">
                      Initializing Alerts...
                    </Text>
                  </Flex>
                ) : (
                  <Button
                    size="sm"
                    leftIcon={<AddIcon />}
                    onClick={handleAddNewAlert}
                    backgroundColor="red.100"
                    color="black"
                    _hover={{ backgroundColor: 'red.200' }}
                  >
                    Add{' '}
                    {practice
                      ? 'Practice Daily Alert'
                      : 'Provider Alert'}
                  </Button>
                )}
              </>
            )}

            {/* Show More/Less button when there are older alerts */}
            {hasAlerts && olderAlerts.length > 0 && (
              <Button
                size="sm"
                leftIcon={
                  isCollapsed ? (
                    <ChevronDownIcon />
                  ) : (
                    <ChevronUpIcon />
                  )
                }
                onClick={() => setIsCollapsed(!isCollapsed)}
                backgroundColor="red.100"
                color="black"
                _hover={{ backgroundColor: 'red.200' }}
              >
                {isCollapsed
                  ? `Show ${olderAlerts.length} more`
                  : 'Hide older alerts'}
              </Button>
            )}
          </Flex>
        </Stack>
      )}

      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Alert
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to delete this alert? This action
              cannot be undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDeleteAlert}
                ml={3}
                isLoading={isUpdating} // Add loader
                loadingText="Deleting..."
                isDisabled={isUpdating} // Disable button while loading
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        size="6xl"
        isCentered
        scrollBehavior="inside"
      >
        <ModalOverlay
          bg="blackAlpha.300"
          backdropFilter="blur(10px)"
        />
        <ModalContent
          maxW="90vw"
          maxH="85vh"
          mx="auto"
          borderRadius="xl"
          boxShadow="2xl"
          display="flex"
          flexDirection="column"
        >
          <ModalHeader
            borderBottom="1px solid"
            borderColor="gray.200"
            pb="1rem"
            bg="gray.50"
            borderTopRadius="xl"
          >
            <HStack justify="space-between" align="center">
              <Text fontSize="1.25rem" fontWeight="600">
                {practice
                  ? 'Practice Daily Alerts'
                  : 'Provider Alerts'}
              </Text>
              <IconButton
                aria-label="Close modal"
                icon={<CloseIcon />}
                onClick={() => setIsEditModalOpen(false)}
                variant="ghost"
                size="sm"
                _hover={{ bg: 'gray.200' }}
              />
            </HStack>
          </ModalHeader>

          <ModalBody
            p="2rem"
            flex="1"
            overflowY="auto"
            minH="0"
            css={{
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#cbd5e0',
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#a0aec0',
              },
            }}
          >
            <VStack spacing="1.5rem" align="stretch">
              {/* Categories Multiselect - Commented out for now, may be needed in future */}
              {/* <Box>
                <HStack spacing={2} align="center" mb="0.5rem">
                  <Text
                    fontSize="0.875rem"
                    fontWeight="500"
                    color="gray.700"
                  >
                    Select Category
                  </Text>
                  <IconButton
                    aria-label="Category Legend"
                    icon={<InfoIcon />}
                    size="xs"
                    variant="ghost"
                    onClick={onCategoryLegendOpen}
                    color="blue.500"
                    h="1.5rem"
                    w="1.5rem"
                    borderRadius="0.375rem"
                    _hover={{
                      bg: 'blue.50',
                      color: 'blue.600',
                    }}
                  />
                </HStack>
                <Text fontSize="0.75rem" color="gray.600" mb="0.5rem">
                  Choose one or more categories that apply to this
                  alert.
                </Text>
                {categoryOptions.length > 0 ? (
                  <Box>
                    <Menu closeOnSelect={false}>
                      <MenuButton
                        as={Button}
                        width="100%"
                        textAlign="left"
                        fontWeight="normal"
                        rightIcon={<ChevronDownIcon />}
                      >
                        {selectedCategories.length > 0
                          ? `${selectedCategories.length} selected`
                          : 'Select categories...'}
                      </MenuButton>
                      <MenuList maxH="300px" overflowY="auto">
                        {categoryOptions.map((option) => (
                          <MenuItem
                            key={option.value}
                            onClick={() => {
                              setSelectedCategories((prev) =>
                                prev.includes(option.value)
                                  ? prev.filter(
                                      (v) => v !== option.value
                                    )
                                  : [...prev, option.value]
                              );
                            }}
                          >
                            <Checkbox
                              isChecked={selectedCategories.includes(
                                option.value
                              )}
                              mr={2}
                              pointerEvents="none"
                            />
                            <Badge
                              colorScheme={getCategoryColor(
                                option.value
                              )}
                              fontSize="xs"
                            >
                              {option.label}
                            </Badge>
                          </MenuItem>
                        ))}
                      </MenuList>
                    </Menu>
                    {selectedCategories.length > 0 && (
                      <Wrap mt={2} spacing={2}>
                        {selectedCategories.map((category) => (
                          <WrapItem key={category}>
                            <Tooltip
                              label={CATEGORY_LEGEND[category as keyof typeof CATEGORY_LEGEND]?.definition || category}
                              hasArrow
                              placement="top"
                              bg="gray.800"
                              color="white"
                              fontSize="sm"
                              px={3}
                              py={2}
                              borderRadius="md"
                              maxW="300px"
                            >
                              <Tag
                                size="md"
                                colorScheme={getCategoryColor(category)}
                                borderRadius="md"
                                cursor="help"
                              >
                                <TagLabel>
                                  {CATEGORY_LEGEND[category as keyof typeof CATEGORY_LEGEND]?.name || category.replace(/_/g, ' ')}
                                </TagLabel>
                                <TagCloseButton
                                  onClick={() =>
                                    setSelectedCategories((prev) =>
                                      prev.filter((c) => c !== category)
                                    )
                                  }
                                  aria-label={`Remove ${category}`}
                                />
                              </Tag>
                            </Tooltip>
                          </WrapItem>
                        ))}
                      </Wrap>
                    )}
                  </Box>
                ) : (
                  <Text fontSize="0.875rem" color="orange.600">
                    Category options are not available. You can still
                    save the alert.
                  </Text>
                )}
              </Box> */}

              <Box>
                <Text
                  mb="0.5rem"
                  fontSize="0.875rem"
                  fontWeight="500"
                  color="gray.700"
                >
                  Alert Message{' '}
                  <span style={{ color: 'red' }}>*</span>
                </Text>
                <Text fontSize="0.75rem" color="gray.600" mb="0.5rem">
                  Enter a clear, actionable alert message. Rich text
                  formatting is supported.
                </Text>
                <RichTextEditor
                  value={tempAlert}
                  onChange={setTempAlert}
                  placeholder="Enter your daily alert message..."
                  minHeight="4rem"
                  maxHeight="20rem"
                />
              </Box>

              <Box>
                <Text
                  mb="0.5rem"
                  fontSize="0.875rem"
                  fontWeight="500"
                  color="gray.700"
                >
                  Start Date <span style={{ color: 'red' }}>*</span>
                </Text>
                <Text fontSize="0.75rem" color="gray.600" mb="0.5rem">
                  When this alert begins. Can be in the past.
                </Text>
                <HStack spacing={2}>
                  <Input
                    type="date"
                    value={startDate || ''}
                    onChange={(e) => setStartDate(e.target.value)}
                    size="md"
                    borderRadius="md"
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    _hover={{ borderColor: 'gray.300' }}
                    _focus={{
                      borderColor: 'blue.500',
                      boxShadow: '0 0 0 1px #3182ce',
                    }}
                    flex={1}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const tomorrow = new Date();
                      tomorrow.setDate(tomorrow.getDate() + 1);
                      // Format date safely to avoid timezone issues
                      const year = tomorrow.getFullYear();
                      const month = String(
                        tomorrow.getMonth() + 1,
                      ).padStart(2, '0');
                      const day = String(tomorrow.getDate()).padStart(
                        2,
                        '0',
                      );
                      setStartDate(`${year}-${month}-${day}`);
                    }}
                    title="Set start date to tomorrow"
                  >
                    Tomorrow
                  </Button>
                </HStack>
              </Box>

              <Box>
                <HStack spacing={4} align="center">
                  <Checkbox
                    isChecked={neverExpire}
                    onChange={(e) => setNeverExpire(e.target.checked)}
                  >
                    Never Expire
                  </Checkbox>
                  <Text fontSize="0.75rem" color="gray.600">
                    Check this if the alert should never automatically
                    expire
                  </Text>
                </HStack>
              </Box>

              {!neverExpire && (
                <Box>
                  <Text
                    mb="0.5rem"
                    fontSize="0.875rem"
                    fontWeight="500"
                    color="gray.700"
                  >
                    End Date <span style={{ color: 'red' }}>*</span>
                  </Text>
                  <Text
                    fontSize="0.75rem"
                    color="gray.600"
                    mb="0.5rem"
                  >
                    When this alert ends. Must be after the Start
                    Date.
                  </Text>
                  <HStack spacing={2}>
                    <Input
                      type="date"
                      value={endDate || ''}
                      onChange={(e) => setEndDate(e.target.value)}
                      size="md"
                      borderRadius="md"
                      bg="white"
                      border="1px solid"
                      borderColor="gray.200"
                      _hover={{ borderColor: 'gray.300' }}
                      _focus={{
                        borderColor: 'blue.500',
                        boxShadow: '0 0 0 1px #3182ce',
                      }}
                      flex={1}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        if (startDate) {
                          const startDateObj = new Date(startDate);
                          const weekLater = new Date(startDateObj);
                          weekLater.setDate(weekLater.getDate() + 7);
                          // Format date safely to avoid timezone issues
                          const year = weekLater.getFullYear();
                          const month = String(
                            weekLater.getMonth() + 1,
                          ).padStart(2, '0');
                          const day = String(
                            weekLater.getDate(),
                          ).padStart(2, '0');
                          setEndDate(`${year}-${month}-${day}`);
                        }
                      }}
                      isDisabled={!startDate}
                      title="Set end date to 1 week from start date"
                    >
                      +1 Week
                    </Button>
                  </HStack>
                </Box>
              )}

              {/* Only show practice association for providers */}
              {provider && (
                <>
                  <Box>
                    <HStack spacing={4} align="center">
                      <Checkbox
                        isChecked={associateWithPractice}
                        onChange={(e) =>
                          setAssociateWithPractice(e.target.checked)
                        }
                      >
                        Associate with Practice
                      </Checkbox>
                      <Text fontSize="0.75rem" color="gray.600">
                        Link this alert to a specific practice
                      </Text>
                    </HStack>
                  </Box>

                  {associateWithPractice && (
                    <Box>
                      <Text
                        mb="0.5rem"
                        fontSize="0.875rem"
                        fontWeight="500"
                        color="gray.700"
                      >
                        Select Practice{' '}
                        <span style={{ color: 'red' }}>*</span>
                      </Text>
                      <Text
                        fontSize="0.75rem"
                        color="gray.600"
                        mb="0.5rem"
                      >
                        Choose which practice this alert is associated
                        with.
                      </Text>
                      {/* Use practices prop as fallback if provider.relatedPractices is not available */}
                      {(provider?.relatedPractices &&
                        provider.relatedPractices.length > 0) ||
                      (practices && practices.length > 0) ? (
                        <Select
                          value={selectedPracticeId}
                          onChange={(e) =>
                            setSelectedPracticeId(e.target.value)
                          }
                          placeholder="Select a practice"
                          size="md"
                          borderRadius="md"
                          bg="white"
                          border="1px solid"
                          borderColor="gray.200"
                          _hover={{ borderColor: 'gray.300' }}
                          _focus={{
                            borderColor: 'blue.500',
                            boxShadow: '0 0 0 1px #3182ce',
                          }}
                        >
                          {/* Use provider.relatedPractices if available, otherwise fall back to practices prop */}
                          {(provider?.relatedPractices &&
                          provider.relatedPractices.length > 0
                            ? provider.relatedPractices
                            : practices || []
                          ).map((practice) => (
                            <option
                              key={practice.id}
                              value={practice.id}
                            >
                              {practice.name}
                            </option>
                          ))}
                        </Select>
                      ) : (
                        <Box>
                          <Text
                            fontSize="0.875rem"
                            color="red.500"
                            mb="2"
                          >
                            No practices available for this provider.
                            {provider?.relatedPractices
                              ? ` (${provider.relatedPractices.length} practices found)`
                              : practices
                                ? ` (${practices.length} practices from prop)`
                                : ' (relatedPractices is undefined)'}
                          </Text>
                          <Text fontSize="0.75rem" color="gray.600">
                            This may be due to:
                          </Text>
                          <Text
                            fontSize="0.75rem"
                            color="gray.600"
                            ml="1rem"
                          >
                            • Provider not assigned to any practices
                          </Text>
                          <Text
                            fontSize="0.75rem"
                            color="gray.600"
                            ml="1rem"
                          >
                            • Practice relationship data not loaded
                          </Text>
                          <Text
                            fontSize="0.75rem"
                            color="gray.600"
                            ml="1rem"
                          >
                            • API configuration issue
                          </Text>
                        </Box>
                      )}
                    </Box>
                  )}
                </>
              )}

              {/* Transfer Outcomes Configuration */}
              {(canEditDailyNotes() ||
                canEditOfficeAlerts() ||
                isAdmin()) &&
                (isSpecialtyOptionsLoading ||
                localSpecialtyLoading ? (
                  <Box
                    p="2rem"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    bg="gray.50"
                    textAlign="center"
                  >
                    <Spinner size="md" color="blue.500" mb="1rem" />
                    <Text fontSize="0.95rem" color="gray.700">
                      Loading specialty options…
                    </Text>
                    <Text
                      fontSize="0.875rem"
                      color="gray.500"
                      mt="0.5rem"
                    >
                      Please wait while we load the available
                      specialties.
                    </Text>
                  </Box>
                ) : (
                  <TransferOutcomesConfig
                    isPracticeLevel={!!practice}
                    transferOutcomes={transferOutcomes}
                    comment={comment}
                    commentLog={commentLog}
                    onTransferOutcomesChange={setTransferOutcomes}
                    onCommentChange={setComment}
                    onAddComment={handleAddComment}
                    isAddingComment={isAddingComment}
                    showSubmitForApproval={
                      canEditDailyNotes() ||
                      canEditOfficeAlerts() ||
                      isAdmin()
                      // Visible to admins and alert editors
                    }
                    hasExistingId={!!editingAlert?.id}
                    showApproveButton={isAdmin()}
                    canApprove={submittedForApproval}
                    approveDisabledReason={approveDisabledReason}
                    onSubmitForApproval={handleSubmitForApproval}
                    onApprove={handleApproveTransferOutcomes}
                    isSubmitting={isSubmittingForApproval}
                    isApproving={isApproving}
                    specialtyOptions={specialtyOptions}
                  />
                ))}
            </VStack>
          </ModalBody>

          <ModalFooter
            borderTop="1px solid"
            borderColor="gray.200"
            pt="1.5rem"
            pb="1.5rem"
            px="2rem"
            bg="gray.50"
            borderBottomRadius="xl"
            flexShrink={0}
            boxShadow="0 -4px 6px -1px rgba(0, 0, 0, 0.1)"
          >
            <HStack spacing="1rem" width="100%" justify="flex-end">
              <Button
                onClick={() => setIsEditModalOpen(false)}
                variant="outline"
                size="md"
                borderRadius="md"
                minW="100px"
                isDisabled={
                  isUpdating || isSubmittingForApproval || isApproving
                }
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveAlert}
                colorScheme="blue"
                size="md"
                borderRadius="md"
                minW="120px"
                isLoading={isUpdating}
                loadingText="Saving..."
                isDisabled={
                  isUpdating || isSubmittingForApproval || isApproving
                }
              >
                Save Alert
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Category Legend Modal */}
      <Modal
        isOpen={isCategoryLegendOpen}
        onClose={onCategoryLegendClose}
        size="xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent maxH="85vh">
          <ModalHeader>Category Legend & Help</ModalHeader>
          <ModalCloseButton />
          <ModalBody
            pb="2rem"
            maxH="calc(85vh - 120px)"
            overflowY="auto"
          >
            <Stack spacing="1rem">
              {Object.entries(CATEGORY_LEGEND).map(
                ([key, { name, definition }]) => (
                  <Flex
                    key={key}
                    gap="1rem"
                    alignItems="flex-start"
                    borderBottom="1px solid"
                    borderColor="gray.200"
                    pb="1rem"
                    _last={{ borderBottom: 'none', pb: 0 }}
                  >
                    <Badge
                      colorScheme={getCategoryColor(key)}
                      fontSize="sm"
                      px={3}
                      py={1}
                      borderRadius="md"
                      flexShrink={0}
                      minW="120px"
                    >
                      {name}
                    </Badge>
                    <Text
                      fontSize="0.875rem"
                      color="gray.700"
                      flex="1"
                    >
                      {definition}
                    </Text>
                  </Flex>
                ),
              )}
            </Stack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default DailyAlertCard;
